import type { Match, Team } from '@uefadigital/panenka';
import { translateEntity, WinnerReason } from '@uefadigital/panenka';
import { PkAvatar, PkBadge, PkButton, PkDivider, PkIcon, PkIdentifier } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import i18n from 'i18next';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetMatchDomesticQuery, useGetMatchesInfoByTeamIdQuery } from 'api/fspApi';
import EQ from 'assets/tournamentsLogo/EQ.svg';
import SCUP from 'assets/tournamentsLogo/SCUP.svg';
import UCL from 'assets/tournamentsLogo/UCL.svg';
import UECL from 'assets/tournamentsLogo/UECL.svg';
import UEL from 'assets/tournamentsLogo/UEL.svg';
import UNL from 'assets/tournamentsLogo/UNL.svg';
import UWCL from 'assets/tournamentsLogo/UWCL.svg';
import WEURO from 'assets/tournamentsLogo/WEURO.svg';

import * as style from './style.module.css';

export interface FormGuideProps {
  matchData: Match;
  isDesktop?: boolean;
  isResultsExpanded?: boolean;
}

interface MatchResultContent {
  res: string;
  img: string;
  competition: string;
}

interface MatchContent {
  letter: string;
  color: string;
  fontClass: string;
}
interface DataMatchResult {
  [key: string]: MatchContent;
}

interface Logos {
  [key: string]: string;
}

const cssPropertiesPrimary: CSSProperties = {
  fontWeight: 'var(--pk-font-weight-m)',
  fontFamily: 'var(--pk-font-base-regular)',
};
const cssPropertiesSecondary: CSSProperties = {
  fontWeight: 'var(--pk-font-weight-l)',
  fontFamily: 'var(--pk-font-base-semibold)',
};

export const FormGuide = ({ isDesktop, matchData, isResultsExpanded = false }: FormGuideProps) => {
  const { t } = useTranslation();
  const IdentifierFontSize = isDesktop ? 'pk-font-size--xl' : 'pk-font-size--s';
  const BadgeSize = isDesktop ? 64 : 56;
  const Title = isDesktop
    ? 'pk-font-size--xl2 pk-font-weight--bolder pk-line-height--sd pk-font-style--normal pr-font-size--14'
    : 'pk-font-size--xl pk-font-weight--bolder pk-line-height--sd pk-font-style--normal pr-font-size--14';
  const ClubName = `${IdentifierFontSize} pk-text--text-01 pk-font-weight--bold`;

  const toDate = matchData?.kickOffTime?.date ?? new Date().toISOString().slice(0, 10);

  const [isExpanded, setIsExpanded] = useState(isResultsExpanded);

  const {
    data: homeTeamMatchesDetails = [],
    error: homeTeamMatchesDetailsError,
    isLoading: homeTeamMatchesDetailsLoading,
  } = useGetMatchesInfoByTeamIdQuery({ teamId: matchData?.homeTeam?.id ?? '', toDate });

  const {
    data: awayTeamMatchesDetails = [],
    error: awayTeamMatchesDetailsError,
    isLoading: awayTeamMatchesDetailsLoading,
  } = useGetMatchesInfoByTeamIdQuery({ teamId: matchData?.awayTeam?.id ?? '', toDate });

  const {
    data: homeTeamDomesticMatchesDetails = [],
    error: homeTeamDomesticMatchesDetailsError,
    isLoading: homeTeamDomesticMatchesDetailsLoading,
  } = useGetMatchDomesticQuery({ teamId: matchData?.homeTeam?.id ?? '', toDate });

  const {
    data: awayTeamDomesticMatchesDetails = [],
    error: awayTeamDomesticMatchesDetailsError,
    isLoading: awayTeamDomesticMatchesDetailsLoading,
  } = useGetMatchDomesticQuery({ teamId: matchData?.awayTeam?.id ?? '', toDate });

  if (!!homeTeamDomesticMatchesDetailsError || !!awayTeamDomesticMatchesDetailsError) {
    return <div />;
  }

  if (homeTeamDomesticMatchesDetailsLoading || awayTeamDomesticMatchesDetailsLoading) {
    return <div />;
  }

  const sortMatchesByDate = (matches: Array<Match>, domesticMatches: Array<Match>) => {
    return [...matches, ...domesticMatches]
      .sort(
        (match, nextMatch) =>
          new Date(nextMatch?.kickOffTime?.date ?? '').getTime() - new Date(match?.kickOffTime?.date ?? '').getTime(),
      )
      .slice(0, 5);
  };

  const recentHomeTeamMatches = sortMatchesByDate(homeTeamMatchesDetails, homeTeamDomesticMatchesDetails);

  const recentAwayTeamMatches = sortMatchesByDate(awayTeamMatchesDetails, awayTeamDomesticMatchesDetails);

  const Logos: Logos = {
    EQ,
    SCUP,
    UCL,
    UECL,
    UEL,
    UNL,
    UWCL,
    WEURO,
  };

  const showLastMatchesResult = (game: Match, winnerTeam: string | undefined) => {
    const matchResult: MatchResultContent = {
      res: 'lost',
      img: game?.competition?.code ? Logos[game?.competition?.code] : '',
      competition: translateEntity(game?.competition)?.name ?? '',
    };

    if (game?.winner?.match.reason === WinnerReason.DRAW) {
      matchResult.res = 'draw';
      return matchResult;
    }

    if (game?.winner?.match?.team?.id === winnerTeam) {
      matchResult.res = 'win';
      return matchResult;
    }

    return matchResult;
  };

  const homeTeamResults = recentHomeTeamMatches.map((el) => showLastMatchesResult(el, matchData?.homeTeam?.id));

  const awayTeamResults = recentAwayTeamMatches.map((el) => showLastMatchesResult(el, matchData?.awayTeam?.id));

  const expandButton = () => {
    return (
      <div className="pr-display--none pk-mt--xl pk-mt-md--xl3 pk-text--right">
        <PkButton
          appearance="text"
          type="button"
          onPkClick={() => {
            setIsExpanded(!isExpanded);
          }}
        >
          <span className="pk-line-height--sm pk-font-size--m">{t('see_results_of_latest_matches')}</span>{' '}
          <PkIcon name={isExpanded ? 'navigation-chevron-up' : 'navigation-chevron-down'} />
        </PkButton>
      </div>
    );
  };

  const displayTeam = (team: Team | undefined, slot: string) => {
    if (!team) {
      return <div />;
    }

    const identifierStyles = slot === 'prefix' ? 'pr-flex-justify-content--end ' : 'pr-flex-justify-content--start';
    const identifierPrintStyles1 = slot === 'prefix' ? '' : 'pk-align-self--end';
    const identifierAlign = slot === 'prefix' ? 'left' : 'right';

    return (
      <div className={`${identifierStyles} ${identifierPrintStyles1} pk-d--flex pr-avoid-break--inside`}>
        <PkIdentifier
          type={'horizontal'}
          alignment={identifierAlign}
          className={clsx('pk-mb--m', 'pr-flex-direction--row', isDesktop && 'pk-d--flex pk-justify-content--center')}
        >
          <span slot={slot}>
            <PkBadge
              alt={team.internationalName ?? ''}
              size={BadgeSize}
              badge-title={translateEntity(team)?.displayName}
              fallbackImage="club-generic-badge"
              src={team.logoUrl}
              className="pr-avatar-size--41"
            />
          </span>
          <span
            slot="primary"
            className={`${ClubName} ${
              slot === 'prefix' ? 'pk-ml--xs' : 'pk-mr--xs'
            } pk-text--wrap pk-text--center pr-font-size--18`}
          >
            {translateEntity(team)?.displayName}
          </span>
        </PkIdentifier>
      </div>
    );
  };

  const dataMatchResult: DataMatchResult = {
    win: { letter: 'won_small', color: '#0A9504', fontClass: 'pk-text--text-04' },
    draw: { letter: 'drawn_small', color: '#DBDDE9', fontClass: 'pk-text--text-02' },
    lost: { letter: 'lost_small', color: '#CB333B', fontClass: 'pk-text--text-04' },
  };

  const getExpandedIdentifier = (primary: Array<string>, secondary: Array<string>, match: MatchResultContent) => {
    return (
      <PkIdentifier alignment="left" className={isDesktop ? 'pk-my--m pr-my--0' : 'pk-mb--m'}>
        <span slot="prefix" className="pk-mr--xs">
          <PkAvatar
            type="text"
            size={isDesktop ? 48 : 40}
            custom-background={dataMatchResult[match.res].color}
            className="pk-mr--m pr-avatar-size--28 pr-avatar-background--gray"
            title={match.competition}
          >
            <span
              className={`${
                dataMatchResult[match.res].fontClass
              } pk-font-size--xl pr-font-size--14 pr-font-color--white`}
              slot="text"
            >
              {t(dataMatchResult[match.res].letter)}
            </span>
            {!!match.img && (
              <PkBadge
                alt={match.competition}
                fallbackImage="club-generic-badge"
                slot="badge"
                badge-title={match.competition}
                size={24}
                src={match.img}
                className="pr-display--none"
              />
            )}
          </PkAvatar>
        </span>
        <span
          slot="primary"
          className={`${
            isDesktop ? 'pk-font-size--l pk-line-height--md' : 'pk-font-size--m pk-line-height--lg'
          } pk-text--ui-05 pk-font-weight--bold pr-font-size--14 pr-font-weight--500`}
        >
          {primary[0]}
        </span>
        <span
          slot="primary"
          className={`${
            isDesktop ? 'pk-font-size--m pk-line-height--sm' : 'pk-font-size--s pk-line-height--md'
          } pk-text--ui-05`}
          style={cssPropertiesPrimary}
        >
          {primary[1]}
        </span>
        <p
          slot="secondary"
          className={`${
            isDesktop ? ' pk-line-height--md' : 'pk-line-height--lg'
          } pk-text--text-02 pk-font-size--s pr-font-size--10`}
          style={cssPropertiesSecondary}
        >
          {secondary[0]}
        </p>
        <p
          slot="secondary"
          className={`${
            isDesktop ? ' pk-line-height--sm' : 'pk-line-height--lg '
          } pk-text--text-02 pk-font-size--m pk-font-weight--normal pr-font-size--12`}
        >
          {secondary[1]}
        </p>
      </PkIdentifier>
    );
  };

  const displayTeamName = (team: Team | undefined) => {
    if (translateEntity(team)?.displayName) {
      return translateEntity(team).displayName;
    }

    return t('team_uppercase');
  };

  const getAdditionalInfo = (match: Match) => {
    const infoObj = {
      additionalInfo: '',
      aggScore: '',
      penaltyScore: '',
    };

    const isPenalty = match?.score?.penalty ?? false;

    if (isPenalty) {
      infoObj.additionalInfo = translateEntity(match?.winner?.match)?.reasonText ?? '';
      infoObj.penaltyScore = `(${match?.score?.penalty?.home} - ${match?.score?.penalty?.away})`;
    }

    if ('aggregate' in (match?.score ?? {}) && 'aggregate' in (match?.score ?? {})) {
      infoObj.additionalInfo = translateEntity(match?.winner?.aggregate)?.reasonTextAbbr ?? '';
      infoObj.aggScore = `${t('aggregate')} ${match?.score?.aggregate?.home} - ${match?.score?.aggregate?.away}`;
    }

    return infoObj;
  };

  const getMatchResults = (H2HMatchesToDisplay: Array<Match>, team: MatchResultContent[]) => {
    return H2HMatchesToDisplay?.map((match: Match, i: number) => {
      const homeTeam = displayTeamName(match?.homeTeam);
      const awayTeam = displayTeamName(match?.awayTeam);
      const score = `${match?.score?.total?.home} - ${match?.score?.total?.away}`;
      const competition = translateEntity(match?.competition)?.name;
      const start = new Date(match?.kickOffTime?.dateTime ?? '');
      const date = start.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });

      const { additionalInfo, aggScore, penaltyScore } = getAdditionalInfo(match);

      const winner = `${aggScore} ${additionalInfo}`;
      const primary = [`${homeTeam} ${score} ${awayTeam}`, ` ${penaltyScore}`];
      const secondary = [`${winner}`, `${date} • ${competition}`];

      return getExpandedIdentifier(primary, secondary, team[i]);
    });
  };

  const displayMatchResults = (arr: Array<MatchResultContent>, position: string) => {
    return arr.map((gameResult: MatchResultContent, i: number) => {
      const circleMargin = position === 'prefix' ? 'pk-mr--s pk-mr-md--m' : 'pk-ml--m';

      return (
        <PkAvatar
          key={i}
          type="text"
          size={48}
          custom-background={dataMatchResult[gameResult.res].color}
          className={`${circleMargin} pk-mb--m pr-avatar-background--gray pr-avatar-size--32`}
          title={gameResult.competition}
        >
          <span
            className={`${
              dataMatchResult[gameResult.res].fontClass
            } pk-font-size--xl pr-font-color--white pr-font-size--16`}
            slot="text"
          >
            {t(dataMatchResult[gameResult.res].letter)}
          </span>
          {!!gameResult.img && (
            <PkBadge
              alt={gameResult.competition}
              fallbackImage="club-generic-badge"
              slot="badge"
              badge-title={gameResult.competition}
              size={24}
              src={gameResult.img}
              className="pr-avatar-size--20"
            />
          )}
        </PkAvatar>
      );
    });
  };

  if (!!homeTeamMatchesDetailsError || !!awayTeamMatchesDetailsError) {
    return <div />;
  }

  if (homeTeamMatchesDetailsLoading || awayTeamMatchesDetailsLoading) {
    return <div />;
  }

  return (
    <div className="pr-avoid-break--inside pk-p--m pk-p-md--xl">
      <div className={`${Title} pk-m--0 pk-mt--xs pk-mt-md--m pk-text--text-01 pr-font-size--16 pk-font--comp-regular`}>
        {t('form_guide')}
      </div>
      <div className="pk-px--0">
        <div className="pk-d--flex pk-justify-content--between pk-flex--wrap pk-mt--m pk-mt-md--xl">
          <div className={`${isDesktop ? 'pk-d-sm--flex pk-justify-content-sm--center' : ''}`}>
            <div className="pk-d-sm--flex pk-justify-content-sm--between pk-flex--column">
              {displayTeam(matchData?.homeTeam, 'prefix')}
              <div className="pr-avoid-break--inside">
                <div className="pk-mb--m">
                  <span className="pk-text--text-01 pk-font-size--s">{t('latest')}</span>
                </div>
                <div className="pk-d-sm--flex pk-flex--wrap pk-justify-content-sm--between">
                  {displayMatchResults(homeTeamResults, 'prefix')}
                </div>
              </div>
            </div>
          </div>
          {!isDesktop && <PkDivider class="divider--horizontal pk-my--xl" direction="horizontal" />}
          <div className={clsx(isDesktop && 'pk-d-sm--flex pk-justify-content-sm--center')}>
            <div className="pk-d-sm--flex pk-justify-content-sm--between pk-flex--column">
              {displayTeam(matchData?.awayTeam, isDesktop ? 'suffix' : 'prefix')}
              <div className="pr-avoid-break--inside">
                <div className="pk-mb--m">
                  <span className="pk-text--text-01 pk-font-size--s">{t('latest')}</span>
                </div>
                <div className="pk-d-sm--flex pk-flex--wrap pk-justify-content-sm--between">
                  {displayMatchResults(awayTeamResults, isDesktop ? 'suffix' : 'prefix')}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="pk-d--none pr-display--block">
          <PkDivider class="divider--horizontal" direction="horizontal" />
        </div>
      </div>
      <div className="pk-mt--xs pk-mt-md--xl2">{isDesktop ? expandButton() : <div />}</div>
      {isExpanded && (
        <>
          <div className="pr-display--none">
            <PkDivider class="divider--horizontal" direction="horizontal" />
          </div>
          <div className="pk-d--flex pk-flex--wrap pk-justify-content--between pk-mt-md--xl pk-overflow--hidden">
            <div className={`${Title} pk-pt--xl pk-d--none pr-display--block`}>{t('previous_results')}</div>
            <div data-id="recent-matches-home" className={style.formGuideMatches}>
              {getMatchResults(recentHomeTeamMatches, homeTeamResults)}
            </div>
            {isDesktop && (
              <div className="pk-mx--xs2">
                <PkDivider class="divider--vertical pk-px--auto" direction="vertical" />
              </div>
            )}
            <div data-id="recent-matches-away" className={style.formGuideMatches}>
              {getMatchResults(recentAwayTeamMatches, awayTeamResults)}
            </div>
            <div className="pk-d--none pr-display--block">
              <PkDivider class="divider--horizontal" direction="horizontal" />
            </div>
          </div>
        </>
      )}
      <div className="pk-mt--m pk-mt-md--l pr-display--none">{isDesktop ? <div /> : expandButton()}</div>
    </div>
  );
};
